import { TrashIcon } from '@heroicons/react/24/outline'
import { Button, Card, CardBody, CardFooter, Dialog, Menu, MenuHandler, MenuItem, MenuList, Typography } from '@material-tailwind/react'
import React, { useEffect, useMemo, useState } from 'react'
import ReactPaginate from 'react-paginate'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { FaDownload, FaEye, FaFilePdf, FaTruck } from 'react-icons/fa'
import { BsThreeDotsVertical } from 'react-icons/bs'

const getStatusColor = (status) => {
  const s = String(status || '').toLowerCase()
  if (s === 'delivered') return 'bg-[#edf9f1] text-[#1fa463] border-[#c8efd6]'
  if (s === 'in-transit') return 'bg-[#f4f0ff] text-[#7c5cff] border-[#ddd2ff]'
  if (s === 'pickedup' || s === 'picked up') return 'bg-[#fff4ed] text-[#f97316] border-[#ffd6bf]'
  if (s === 'new') return 'bg-[#fff8e8] text-[#c98709] border-[#fde7a2]'
  if (s === 'undelivered') return 'bg-[#fff1f2] text-[#e11d48] border-[#fecdd3]'
  return 'bg-gray-100 text-gray-800 border-gray-200'
}

const isDelivered = (status) => String(status || '').toLowerCase() === 'delivered'

const OrderTable = ({ filteredOrderList, handlePartnerModal, setOrderID, deleteOrder, loadingOrder, searchQuery, currentPage, setCurrentPage, rowsPerPage, setRowsPerPage, selectedOrderIds, toggleOrderSelection, toggleSelectAllVisible, activeTab }) => {
  const { userProfile } = useSelector((state) => state.user)
  const [showAdd, setShowAdd] = useState('')
  const [showReasonModal, setShowReasonModal] = useState(false)
  const [reasonData, setReasonData] = useState({ reason: '', image: '' })

  const isAdmin = userProfile?.role === 'admin'
  const showSelection = true
  const showDeliveryDateColumn = activeTab === 'delivered'
  const totalItems = filteredOrderList?.length || 0
  const pageCount = Math.max(1, Math.ceil(totalItems / rowsPerPage))
  const safePage = totalItems === 0 ? 0 : Math.min(currentPage, pageCount - 1)
  const startIndex = safePage * rowsPerPage
  const currentItems = useMemo(
    () => (filteredOrderList || []).slice(startIndex, startIndex + rowsPerPage),
    [filteredOrderList, rowsPerPage, startIndex]
  )
  const visibleOrderIds = currentItems.map(item => item?.id).filter(Boolean)
  const allVisibleSelected = visibleOrderIds.length > 0 && visibleOrderIds.every(id => selectedOrderIds?.includes(id))

  useEffect(() => {
    if (loadingOrder) return
    if (totalItems === 0) return
    if (safePage !== currentPage) {
      setCurrentPage(safePage)
    }
  }, [currentPage, loadingOrder, safePage, setCurrentPage, totalItems])

  const openReason = (item) => {
    setReasonData({
      reason: item?.undeliveredReason || 'N/A',
      image: item?.undeliveredPod || '',
    })
    setShowReasonModal(true)
  }

  const handlePageClick = (event) => {
    setCurrentPage(event.selected)
  }

  return (
    <>
      <Dialog size="sm" open={showReasonModal} handler={() => setShowReasonModal(false)} className="bg-transparent shadow-none">
        <Card className="w-full">
          <CardBody>
            <Typography className='mb-2 text-[1.1rem] font-[600]'>Undelivered Reason</Typography>
            <Typography className='mb-4 text-[0.9rem]'>{reasonData.reason}</Typography>
            {reasonData.image && (
              <div className='w-full'>
                <img src={reasonData.image} alt='Undelivered' className='max-h-72 rounded border border-gray-300 object-contain' />
              </div>
            )}
          </CardBody>
          <CardFooter className='flex justify-end border-t border-gray-200'>
            <Button onClick={() => setShowReasonModal(false)}>Close</Button>
          </CardFooter>
        </Card>
      </Dialog>

      <div className='overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-[0_18px_42px_rgba(15,23,42,0.07)]'>
        <div className='overflow-x-auto'>
          <table className='min-w-full border-separate border-spacing-0'>
            <thead>
              <tr className='bg-[#f8fafc]'>
                {showSelection && (
                  <th className='border-b border-slate-200 px-4 py-4 text-center'>
                    <input
                      type="checkbox"
                      className='h-4 w-4 rounded border-slate-300'
                      checked={allVisibleSelected}
                      onChange={(e) => toggleSelectAllVisible(visibleOrderIds, e.target.checked)}
                    />
                  </th>
                )}
                <th className='border-b border-slate-200 px-4 py-4 text-left text-xs font-[GilroyBold] uppercase tracking-[0.14em] text-slate-500'>Order</th>
                <th className='border-b border-slate-200 px-4 py-4 text-left text-xs font-[GilroyBold] uppercase tracking-[0.14em] text-slate-500'>Booked</th>
                <th className='border-b border-slate-200 px-4 py-4 text-left text-xs font-[GilroyBold] uppercase tracking-[0.14em] text-slate-500'>Client</th>
                <th className='border-b border-slate-200 px-4 py-4 text-left text-xs font-[GilroyBold] uppercase tracking-[0.14em] text-slate-500'>Consignee</th>
                <th className='border-b border-slate-200 px-4 py-4 text-left text-xs font-[GilroyBold] uppercase tracking-[0.14em] text-slate-500'>Route</th>
                {showDeliveryDateColumn && (
                  <th className='border-b border-slate-200 px-4 py-4 text-left text-xs font-[GilroyBold] uppercase tracking-[0.14em] text-slate-500'>Delivery Date</th>
                )}
                <th className='border-b border-slate-200 px-4 py-4 text-center text-xs font-[GilroyBold] uppercase tracking-[0.14em] text-slate-500'>Status</th>
                <th className='border-b border-slate-200 px-4 py-4 text-center text-xs font-[GilroyBold] uppercase tracking-[0.14em] text-slate-500'>Action</th>
              </tr>
            </thead>

            <tbody>
              {loadingOrder ? (
                <tr>
                  <td colSpan={(showSelection ? 8 : 7) + (showDeliveryDateColumn ? 1 : 0)} className='px-4 py-16 text-center text-sm text-slate-500'>
                    Loading orders...
                  </td>
                </tr>
              ) : currentItems.length === 0 ? (
                <tr>
                  <td colSpan={(showSelection ? 8 : 7) + (showDeliveryDateColumn ? 1 : 0)} className='px-4 py-16 text-center'>
                    <p className='text-base font-[GilroyMedium] text-slate-500'>No orders found</p>
                    <p className='mt-2 text-xs text-slate-400'>
                      {searchQuery ? 'Try adjusting your search or filters.' : 'Orders will appear here once available.'}
                    </p>
                  </td>
                </tr>
              ) : (
                currentItems.map((item) => (
                  <tr key={item?.id} className='transition-colors hover:bg-slate-50/70'>
                    {showSelection && (
                      <td className='border-b border-slate-100 px-4 py-4 text-center align-middle'>
                        <input
                          type="checkbox"
                          className='h-4 w-4 rounded border-slate-300'
                          checked={selectedOrderIds?.includes(item?.id)}
                          onChange={() => toggleOrderSelection(item?.id)}
                        />
                      </td>
                    )}

                    <td className='border-b border-slate-100 px-4 py-4 align-middle'>
                      <Link to={`/orderDetails/${item?.lrno}`}>
                        <p className='text-[13px] font-[GilroyBold] text-slate-900 transition-colors hover:text-[#f97316]'>
                          {item?.lrno || 'N/A'}
                        </p>
                      </Link>
                      <p className='mt-1 text-[11px] font-[GilroyMedium] text-slate-400'>
                        {item?.orderId || item?.id}
                      </p>
                    </td>

                    <td className='border-b border-slate-100 px-4 py-4 align-middle'>
                      <p className='text-[11px] font-[GilroyMedium] text-slate-700'>
                        {new Date(item?.createdAt).toLocaleDateString('en-GB')}
                      </p>
                      <p className='mt-1 text-[11px] font-[GilroyMedium] text-slate-400'>
                        {new Date(item?.createdAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </td>

                    <td className='border-b border-slate-100 px-4 py-4 align-middle'>
                      <p className='max-w-[170px] truncate text-[13px] font-[GilroyMedium] text-slate-800' title={item?.userName || 'N/A'}>
                        {item?.userName || 'N/A'}
                      </p>
                    </td>

                    <td className='border-b border-slate-100 px-4 py-4 align-middle'>
                      {showAdd === item?.lrno ? (
                        <div className='relative max-w-[200px] pr-5'>
                          <button
                            onClick={() => setShowAdd('')}
                            className='absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-xs text-white'
                          >
                            ×
                          </button>
                          <p className='text-[11px] font-[GilroyMedium] text-slate-600'>
                            {item?.dropoff_location?.address || 'N/A'}
                          </p>
                        </div>
                      ) : (
                        <p
                          className='max-w-[200px] cursor-pointer truncate text-[13px] font-[GilroyMedium] text-slate-800 transition-colors hover:text-[#f97316]'
                          onClick={() => setShowAdd(item?.lrno)}
                          title={item?.dropoff_location?.consignee || item?.dropoff_location?.companyName || 'Click to view address'}
                        >
                          {item?.dropoff_location?.consignee || item?.dropoff_location?.companyName || 'N/A'}
                        </p>
                      )}
                    </td>

                    <td className='border-b border-slate-100 px-4 py-4 align-middle'>
                      <p className='text-[13px] font-[GilroyMedium] text-slate-800'>
                        {item?.pickup_location?.city || 'N/A'}{item?.pickup_location?.state ? `, ${item?.pickup_location?.state}` : ''}
                      </p>
                      <p className='mt-1 text-[11px] font-[GilroyMedium] text-slate-400'>
                        to {item?.dropoff_location?.city || 'N/A'}{item?.dropoff_location?.region ? `, ${item?.dropoff_location?.region}` : ''}
                      </p>
                    </td>

                    {showDeliveryDateColumn && (
                      <td className='border-b border-slate-100 px-4 py-4 align-middle'>
                        {isDelivered(item?.orderStatus) && item?.deliveryactualdate ? (
                          <>
                            <p className='text-[13px] font-[GilroyBold] text-slate-900'>
                              {new Date(item.deliveryactualdate).toLocaleDateString('en-GB')}
                            </p>
                            <p className='mt-1 text-[11px] font-[GilroyMedium] text-slate-400'>
                              {new Date(item.deliveryactualdate).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                            </p>
                          </>
                        ) : (
                          <p className='text-[11px] font-[GilroyMedium] text-slate-400'>-</p>
                        )}
                      </td>
                    )}

                    <td className='border-b border-slate-100 px-4 py-4 text-center align-middle'>
                      <span className={`inline-flex rounded-full border px-2.5 py-1 text-[11px] font-[GilroyMedium] ${getStatusColor(item?.orderStatus)}`}>
                        {item?.orderStatus || 'N/A'}
                      </span>
                    </td>

                    <td className='border-b border-slate-100 px-4 py-4 text-center align-middle'>
                      <Menu placement="bottom-end">
                        <MenuHandler>
                          <Button variant="text" className='rounded-full p-1.5 text-slate-600 shadow-none hover:bg-slate-100'>
                            <BsThreeDotsVertical className='h-3.5 w-3.5' />
                          </Button>
                        </MenuHandler>
                        <MenuList className='min-w-[200px]'>
                          {item?.orderStatus === 'new' && (
                            <MenuItem
                              onClick={() => {
                                setOrderID(item?.id)
                                handlePartnerModal()
                              }}
                              className='flex items-center gap-2'
                            >
                              <FaTruck className='h-4 w-4' />
                              Ship Now
                            </MenuItem>
                          )}
                          {String(item?.orderStatus).toLowerCase() === 'delivered' && item?.pod && (
                            <MenuItem
                              as="a"
                              href={item?.pod}
                              target='_blank'
                              rel='noopener noreferrer'
                              download={item?.pod}
                              className='flex items-center gap-2'
                            >
                              <FaEye className='h-4 w-4' />
                              View POD
                            </MenuItem>
                          )}
                          {String(item?.orderStatus).toLowerCase() === 'undelivered' && (
                            <MenuItem onClick={() => openReason(item)} className='flex items-center gap-2 text-red-600'>
                              <FaEye className='h-4 w-4' />
                              View Undelivered Reason
                            </MenuItem>
                          )}

                          {(item?.orderStatus === 'new' ||
                            (String(item?.orderStatus).toLowerCase() === 'delivered' && item?.pod) ||
                            String(item?.orderStatus).toLowerCase() === 'undelivered') && (
                            <hr className='my-2 border-gray-200' />
                          )}

                          <Link to={`/orderDetails/waybill/${item?.id}`}>
                            <MenuItem className='flex items-center gap-2'>
                              <FaFilePdf className='h-4 w-4' />
                              Download Waybill
                            </MenuItem>
                          </Link>
                          <Link to={`/orderDetails/shippinglabel/${item?.id}`}>
                            <MenuItem className='flex items-center gap-2'>
                              <FaDownload className='h-4 w-4' />
                              Download Shipping Label
                            </MenuItem>
                          </Link>
                          <Link to={`/orderDetails/${item?.lrno}`}>
                            <MenuItem className='flex items-center gap-2'>
                              <FaEye className='h-4 w-4' />
                              View Details
                            </MenuItem>
                          </Link>
                          {isAdmin && (
                            <>
                              <hr className='my-2 border-gray-200' />
                              <MenuItem
                                onClick={() => {
                                  if (window.confirm('Are you sure you want to delete this order?')) {
                                    deleteOrder(item?.id)
                                  }
                                }}
                                className='flex items-center gap-2 text-red-600'
                              >
                                <TrashIcon className='h-4 w-4' />
                                Delete Order
                              </MenuItem>
                            </>
                          )}
                        </MenuList>
                      </Menu>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalItems > 0 && (
          <div className='border-t border-slate-200 bg-slate-50 px-4 py-3'>
            <div className='flex flex-col gap-3 md:flex-row md:items-center md:justify-between'>
              <div className='flex items-center gap-3'>
                <span className='text-[12px] font-[GilroyMedium] text-slate-500'>Rows</span>
                <select
                  value={rowsPerPage}
                  onChange={(event) => {
                    setRowsPerPage(Number(event.target.value))
                    setCurrentPage(0)
                  }}
                  className='rounded-full border border-slate-200 bg-white px-3 py-2 text-[12px] font-[GilroyBold] text-slate-700 focus:outline-none'
                >
                  {[5, 10, 50, 100].map((option) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>

              {totalItems > rowsPerPage && (
                <ReactPaginate
                  breakLabel="..."
                  nextLabel="Next"
                  onPageChange={handlePageClick}
                  pageRangeDisplayed={2}
                  pageCount={pageCount}
                  previousLabel="Prev"
                  forcePage={safePage}
                  renderOnZeroPageCount={null}
                  containerClassName='flex flex-wrap items-center justify-start md:justify-end gap-2'
                  pageClassName='list-none'
                  pageLinkClassName='flex h-8 min-w-[34px] items-center justify-center rounded-full border border-slate-200 bg-white px-3 text-xs font-[GilroyMedium] text-slate-600 transition-colors hover:border-[#f97316] hover:text-[#f97316]'
                  previousClassName='list-none'
                  previousLinkClassName='flex h-8 items-center justify-center rounded-full border border-slate-200 bg-white px-3 text-xs font-[GilroyMedium] text-slate-600 transition-colors hover:border-[#f97316] hover:text-[#f97316]'
                  nextClassName='list-none'
                  nextLinkClassName='flex h-8 items-center justify-center rounded-full border border-slate-200 bg-white px-3 text-xs font-[GilroyMedium] text-slate-600 transition-colors hover:border-[#f97316] hover:text-[#f97316]'
                  breakClassName='list-none'
                  breakLinkClassName='flex h-8 min-w-[34px] items-center justify-center rounded-full text-xs text-slate-400'
                  activeLinkClassName='!border-[#f97316] !bg-[#f97316] !text-white'
                  disabledLinkClassName='cursor-not-allowed opacity-50'
                />
              )}
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export default OrderTable
